import React, { useContext, useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Input, Label, Row, } from "reactstrap";
import _ from "lodash";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { ServiceThreadSpinner, AttachmentManager } from "../";
import { api, constants, helpers, dateHelpers, UserContext } from "../../utils";
import { DesktopContainer } from "../../layouts";
import axios from "axios";
import { toast } from "react-toastify";
import DebounceInput from "react-debounce-input";

const status = {
  Open: 1,
  Closed: { label: "Investigation Completed", value: 2 },
  Canceled: { label: "Investigation Canceled", value: 5 },
};
const blankForm = {
  action: "",
  actionId: 0,
  actionType: 0,
  attachmentType: 0,
  caseNumber: "", // -- multi
  completedBy: "",
  completionDate: null,
  createdDate: "",
  customerId: "",
  customerIdName: "",
  displayId: "",
  followUp: "",
  followUpBy: "",
  followUpDate: null,
  invoiceNumber: "", // -- invoices
  originator: "",
  problemDescription: "",
  problemTypeDescription: "",
  problemTypeId: 0,
  productType: "", // -- customer part number
  productionOperator: "",
  quantity: 0,
  rootCause: "",
  source: null,
  status: 1,
  statusDescription: "",
  targetDate: "",
  targetFollowUpDate: "",
  unitOfMeasure: "",
  customerIdSelect: null,
  invoiceNumberSelect: null,
  productTypeSelect: null,
  caseNumberSelect: null,
  problemTypeIdSelect: null,
  followUpBySelect: null,
};

function ColPrompt({ title }) {
  return (
    <Col xs="3" className="text-white">
      <h5 style={{ textAlign: "right" }}>{title}:</h5>
    </Col>
  );
}

function RadioOption({ onChange, currentValue, classType, fieldName }) {
  return (
    <Col xs="2" className="text-white">
      <Label>
        <Input
          type="radio"
          name={fieldName}
          onChange={onChange}
          onClick={onChange}
          defaultValue={currentValue === classType.value}
          checked={currentValue === classType.value}
        />
      </Label>
      <span className="ms-2">
        {classType.label ? classType.label : classType}
      </span>
    </Col>
  );
}

// Declared at module scope so its function reference is stable across renders of
// the parent component. A nested declaration creates a new component identity on
// every parent re-render, which remounts AttachmentManager and re-fires its
// initial GetList HTTP request on every onChange. See Phase 5 artifact 13.
function AttachmentRow({ displayId }) {
  if (!displayId) return null;
  return (
    <Row>
      <Col xxl={{ size: 8, offset: 2 }}>
        <Card className="dashboard-card">
          <CardBody>
            <AttachmentManager
              attachmentTypeId={constants.ATTACHMENT_TYPES.InternalCorrectiveAction}
              contextName={displayId}
              attachmentKey={displayId}
            />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
}

export default function InternalCorrectiveAction(props) {
  const cancelToken = axios.CancelToken;
  const navigate = useNavigate();
  const source = cancelToken.source();
  const userCtx = useContext(UserContext);
  const { actionId } = useParams();
  const [currentActionDto, setCurrentActionDto] = useState(blankForm);
  const [isDisabled, setIsDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [staticSelectList, setStaticSelectList] = useState({
    problemTypes: [],
    followByUsers: [],
  });

  useEffect(() => {
    if (!_.isNil(actionId)) {
      setIsLoading(true);
      api
        .postS(
          "Quality/FetchInternalAction",
          { actionId: actionId },
          source.Token
        )
        .then((r) => {
          if (r.data.success) {
            let coa = r.data.internalAction;
            if (
              coa.status !== status.Open &&
              userCtx &&
              userCtx.currentUser &&
              !userCtx.currentUser.isAdmin
            )
              setIsDisabled(true);
            setCurrentActionDto({
              ...currentActionDto,
              ...coa,
              targetDate: dateHelpers.formatDateForServer(coa.targetDate),
              targetFollowUpDate: dateHelpers.formatDateForServer(
                coa.targetFollowUpDate
              ),
            });
            setStaticSelectList({
              problemTypes: coa.problemTypes.map(item => item.active === false ? { ...item, isDisabled: true } : item),
              followByUsers: coa.followByUsers,
            });
          } else {
            toast.error(r.data.message);
          }
        })
        .catch(api.catchHandler)
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      console.error("actionId is not here");
    }

    return () => {
      source.cancel("axios request cancelled");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  const getCommaDelimitedString = (list) => {
    let result = "";
    _.map(list, (x) => {
      if (typeof x.value === "string") {
        result += `${x.value.trim()},`;
      }
    });
    result = result.slice(0, -1);
    return result;
  };

  const onChange = (property, value, secProp = null, secValue = null) => {
    let copy = _.cloneDeep(currentActionDto);
    if (!_.isNil(secProp) && !_.isNil(secValue)) _.set(copy, secProp, secValue);
    _.set(copy, property, value);
    setCurrentActionDto(copy);
  };

  const onChangeStatus = (property, value) => {
    if (value === currentActionDto.status) value = 1;
    let copy = _.cloneDeep(currentActionDto);
    _.set(copy, property, value);
    setCurrentActionDto(copy);
  };

  const runReport = () => {
    //send the primary key action Id and the action Type to decide which report to retieve
    setIsLoading(true);
    api
      .postDownloadBinary(
        "Report/CorrectiveAction",
        {
          actionType: currentActionDto.actionType,
          actionId: currentActionDto.actionId,
        },
        constants.MIME_TYPES.pdf,
        currentActionDto.displayId + ".pdf",
        "There was an error downloading this corrective action pdf"
      )
      .then((res) => {
        if (res?.data === false) {
          toast.error(res.message);
        }
      })
      .catch((error) => {
        api.catchHandler(error);
      })
      .finally(() => setIsLoading(false));
  };

  const SaveCorrectiveAction = () => {
    let copy = _.cloneDeep(currentActionDto);
    let payload = {};
    for (let key in copy) {
      let value = copy[key];
      if (_.includes(key, "Selec")) {
        let newKey = key.slice(0, key.length - 6);
        let newValue = value;
        if (_.isArray(value) && value.length) {
          newValue = getCommaDelimitedString(value);
        } else {
          newValue = helpers.valueFromSelect(value);
          newValue = _.trim(newValue);
        }
        Object.assign(payload, { [newKey]: newValue });
      } else {
        Object.assign(payload, { [key]: value });
      }
    }
    const validated = ValidatePayload(payload);
    if (validated) {
      api
        .post("Quality/SaveCorrectiveAction", payload)
        .then((r) => {
          if (r.data.success) {
            navigate(0);
            toast.info("Successfully updated");
          } else {
            toast.error("Something isn't right... Please try again later");
            throw new Error(r.data.message);
          }
        })
        .catch(api.catchHandler);
    }
  };

  const ValidatePayload = (payload) => {
    let errors = "";
    if (payload.quantity === "") payload.quantity = 0;
    if (payload.problemTypeId === "") payload.problemTypeId = 0;
    if (payload.status === "") payload.status = 1;

    // if(typeof(payload.quantity) === "string") payload.quantity = parseInt(payload.quantity ?? "0");
    if (typeof payload.problemTypeId === "string")
      payload.problemTypeId = parseInt(payload.problemTypeId ?? "0");

    if (payload.quantity === 0 || payload.quantity < 0)
      errors = errors.concat("<br />Quantity is required");

    if (!payload.problemDescription || _.isEmpty(payload.problemDescription))
      errors = errors.concat(
        "<br />Problem Description is required to mark the action complete or canceled"
      );
    if (payload.status !== 1) {
      if (!payload.rootCause || _.isEmpty(payload.rootCause))
        errors = errors.concat(
          "<br />Root Cause is required to mark the action complete or canceled"
        );
      if (!payload.action || _.isEmpty(payload.action))
        errors = errors.concat(
          "<br />Corrective Action Taken is required to mark the action complete or canceled"
        );
    }
    if (_.isEmpty(errors)) return true;
    toast.error(errors);
    return false;
  };

  return (
    <DesktopContainer screenName={""}>
      <ServiceThreadSpinner on={isLoading}>
        <form>
          <fieldset disabled={isDisabled}>
            <div className="filter-header">
              <Row className="newCorrectiveAction">
                <Col xs={3} />
                <Col xs={6} className="text-white">
                  <h5>
                    {currentActionDto.displayId
                      ? currentActionDto.displayId
                      : "Determined upon save"}
                  </h5>
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Originator" />
                <Col xs={6} className="text-white">
                  <h5>{currentActionDto.originator}</h5>
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Created Date" />
                <Col xs={6} className="text-white">
                  <h5>
                    {dateHelpers.toLongDateString(currentActionDto.createdDate)}
                  </h5>
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Target Completion Date" />
                <Col xs={6} className="text-white">
                  <DebounceInput
                    type="date"
                    onChange={(e) => onChange("targetDate", e.target.value)}
                    value={currentActionDto.targetDate}
                    debounceTimeout={500}
                  />
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Target Follow-up Date" />
                <Col xs={6} className="text-white">
                  <DebounceInput
                    type="date"
                    onChange={(e) =>
                      onChange("targetFollowUpDate", e.target.value)
                    }
                    value={currentActionDto.targetFollowUpDate}
                    debounceTimeout={500}
                  />
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Quantity" />
                <Col xs={6} className="text-white">
                  <Input
                    type="number"
                    value={currentActionDto.quantity}
                    onChange={(e) => onChange("quantity", e.target.value)}
                    placeholder={"Enter Quantity..."}
                  />
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Problem Type" />
                <Col xs={6}>
                  <Select
                    classNamePrefix="serviceThreadSelect"
                    options={staticSelectList.problemTypes}
                    value={currentActionDto.problemTypeIdSelect}
                    onChange={(opt) => onChange("problemTypeIdSelect", opt)}
                    isDisabled={isDisabled}
                  />
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Production Operator" />
                <Col xs={6} className="text-white">
                  <Input
                    type="text"
                    value={currentActionDto.productionOperator}
                    onChange={(e) =>
                      onChange("productionOperator", e.target.value)
                    }
                    placeholder={"Enter Operator..."}
                  />
                </Col>
              </Row>
              <Row
                children={
                  <Col
                    xs={{ size: 6, offset: 3 }}
                    className="text-white"
                    children={"Problem Description:"}
                  />
                }
              />
              <Row className="newCorrectiveAction">
                <Col xs={{ size: 6, offset: 3 }} className="text-white">
                  <Input
                    type="textarea"
                    className="serviceThreadTextArea"
                    value={currentActionDto.problemDescription}
                    maxLength={255}
                    style={{ height: "150px", marginTop: "4px" }}
                    onChange={(e) =>
                      onChange("problemDescription", e.target.value)
                    }
                    placeholder={"Enter Description..."}
                  />
                </Col>
              </Row>
              <Row
                children={
                  <Col
                    xs={{ size: 6, offset: 3 }}
                    className="text-white"
                    children={
                      "Investigate and Determine Root Cause of Problem:"
                    }
                  />
                }
              />
              <Row className="newCorrectiveAction">
                <Col xs={{ size: 6, offset: 3 }} className="text-white">
                  <Input
                    type="textarea"
                    className="serviceThreadTextArea"
                    value={currentActionDto.rootCause}
                    maxLength={255}
                    style={{ height: "150px", marginTop: "4px" }}
                    onChange={(e) => onChange("rootCause", e.target.value)}
                    placeholder={"Enter Description..."}
                  />
                </Col>
              </Row>
              <Row
                children={
                  <Col
                    xs={{ size: 6, offset: 3 }}
                    className="text-white"
                    children={"Corrective Action Taken:"}
                  />
                }
              />
              <Row className="newCorrectiveAction">
                <Col xs={{ size: 6, offset: 3 }} className="text-white">
                  <Input
                    type="textarea"
                    className="serviceThreadTextArea"
                    value={currentActionDto.action}
                    maxLength={255}
                    style={{ height: "150px", marginTop: "4px" }}
                    onChange={(e) => onChange("action", e.target.value)}
                    placeholder={"Enter Description..."}
                  />
                </Col>
              </Row>
              <Row className="newProductionItemLine">
                <Col xs="3" />
                <RadioOption
                  classType={status.Closed}
                  currentValue={currentActionDto.status}
                  onChange={() =>
                    onChangeStatus("status", status.Closed?.value)
                  }
                  fieldName="correctiveStatus"
                />
                <RadioOption
                  classType={status.Canceled}
                  currentValue={currentActionDto.status}
                  onChange={() =>
                    onChangeStatus("status", status.Canceled?.value)
                  }
                  fieldName="correctiveStatus"
                />
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Completed By" />
                <Col xs={6} className="text-white">
                  <h5>{currentActionDto.completedBy}</h5>
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Completion Date" />
                <Col xs={6} className="text-white">
                  <h5>
                    {currentActionDto.completionDate
                      ? dateHelpers.toLongDateString(
                          currentActionDto.completionDate
                        )
                      : null}
                  </h5>
                </Col>
              </Row>
              <hr />
              <Row
                children={
                  <Col
                    xs={{ size: 6, offset: 3 }}
                    className="text-white"
                    children={"Follow-up Comments:"}
                  />
                }
              />
              <Row className="newCorrectiveAction">
                <Col xs={{ size: 6, offset: 3 }} className="text-white">
                  <Input
                    type="textarea"
                    className="serviceThreadTextArea"
                    value={currentActionDto.followUp}
                    maxLength={255}
                    style={{ height: "150px", marginTop: "4px" }}
                    onChange={(e) => onChange("followUp", e.target.value)}
                    placeholder={"Enter Description..."}
                  />
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Follow-up By" />
                <Col xs={6}>
                  <Select
                    classNamePrefix="serviceThreadSelect"
                    options={staticSelectList.followByUsers}
                    value={currentActionDto.followUpBySelect}
                    onChange={(opt) => onChange("followUpBySelect", opt)}
                    isDisabled={isDisabled}
                  />
                </Col>
              </Row>
              <Row className="newCorrectiveAction">
                <ColPrompt title="Follow-up Date" />
                <Col xs={6} className="text-white">
                  <DebounceInput
                    type="date"
                    onChange={(e) => onChange("followUpDate", e.target.value)}
                    value={dateHelpers.formatDateForServer(
                      currentActionDto.followUpDate
                    )}
                    debounceTimeout={500}
                  />
                </Col>
              </Row>
              <hr />
              <AttachmentRow displayId={currentActionDto.displayId} />
              <Row>
                <div className="inline d-flex justify-content-center">
                  <Button
                    className="shredButton"
                    children={"Save Changes"}
                    onClick={() => SaveCorrectiveAction()}
                  />
                  <Button
                    className="shredButton mx-5"
                    children={"Print Report"}
                    onClick={() => runReport()}
                  />
                </div>
              </Row>
            </div>
          </fieldset>
        </form>
        <Row className="mt-3">
          <div className="inline d-flex justify-content-center">
            <Button
              className="shredButton"
              children={"Cancel Return To Search"}
              onClick={() =>
                navigate(constants.ENDPOINTS.correctiveActionSearch)
              }
            />
          </div>
        </Row>
      </ServiceThreadSpinner>
    </DesktopContainer>
  );
}
